import Breadcrumbs from "@mui/material/Breadcrumbs";
import Link from "@mui/material/Link";
import Stack from "@mui/material/Stack";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { Link as RouterLink } from "react-router-dom";
import { styled } from "../../../../lib/styled";
import type { ReactNode } from "react";
import { gaps } from "../../spacing";
import { PageTitle } from "../../data-display/PageTitle";
import { Subtitle } from "../../data-display/Subtitle";

/** A page the current one is nested in. */
export interface PageBreadcrumb {
  label: string;
  to: string;
}

interface PageHeaderProps {
  title: ReactNode;
  subtitle?: ReactNode;
  /** Leading slot, e.g. a back button. Kept as a slot so this stays domain-free. */
  leading?: ReactNode;
  /** The pages this one is nested in, outermost first. They are shown before the title as a
   *  trail, so the title is the last step of it. */
  breadcrumbs?: PageBreadcrumb[];
  /** The page's primary actions, at the end of the header. They keep their size however long the
   *  title and subtitle are. */
  actions?: ReactNode;
  align?: "left" | "center";
  /** `compact` for a page that sits beside a navigation and shares the screen with it. */
  size?: "default" | "compact";
}

const HeaderRow = styled(Stack)({
  flexWrap: "wrap",
  alignItems: "flex-start",
  justifyContent: "space-between",
});

const HeaderStack = styled(Stack)<{ $align: "left" | "center" }>(({ $align }) => ({
  // Never narrower than a readable column: the actions drop below instead of squeezing the text.
  flex: "1 1 16rem",
  minWidth: 0,
  alignItems: $align === "center" ? "center" : "stretch",
  textAlign: $align,
}));

const Trail = styled(Breadcrumbs)(({ theme }) => ({
  color: theme.palette.text.secondary,
}));

const Actions = styled(Stack)({
  flexShrink: 0,
  flexDirection: "row",
  alignItems: "center",
});

/** Page heading block: an optional leading slot, the page title (after its breadcrumbs, when it is
 *  nested), an optional subtitle, and the page's actions at the end. */
export function PageHeader({
  title,
  subtitle,
  leading,
  breadcrumbs = [],
  actions,
  align = "left",
  size = "default",
}: PageHeaderProps) {
  return (
    <HeaderRow direction="row" gap={gaps.base}>
      <HeaderStack gap={gaps.snug} $align={align}>
        {leading}
        {breadcrumbs.length > 0 ? (
          <Trail aria-label="Breadcrumb" separator={<ChevronRightIcon fontSize="small" />}>
            {breadcrumbs.map(({ label, to }) => (
              <Link
                key={to}
                component={RouterLink}
                to={to}
                variant="h4"
                color="inherit"
                underline="hover"
              >
                {label}
              </Link>
            ))}
            <PageTitle variant={size === "compact" ? "h5" : "h4"} color="text.primary">
              {title}
            </PageTitle>
          </Trail>
        ) : (
          <PageTitle variant={size === "compact" ? "h5" : "h4"}>{title}</PageTitle>
        )}
        {subtitle ? <Subtitle>{subtitle}</Subtitle> : null}
      </HeaderStack>
      {actions ? <Actions gap={gaps.snug}>{actions}</Actions> : null}
    </HeaderRow>
  );
}
